import { Injectable } from '@angular/core'
import { Store } from '@ngxs/store'
import { BehaviorSubject, distinctUntilChanged, filter, map } from 'rxjs'
import { app_version_number } from 'src/app/globals'
import { WhatsNewEntry, unseen } from 'src/app/domain/whats-new'
import { UserState } from 'src/app/state-man/state/user.state'
import { ApiService } from './api.service'
import { LoggerService } from './logger.service'

const CACHE_KEY = 'rt-whats-new'
const SEEN_KEY = 'rt-whats-new-seen'

interface Held { readonly version: string; readonly entries: readonly WhatsNewEntry[] }
const NONE: Held = { version: '', entries: [] }

/**
 * The "What's New" notes (rt2-app/whats-new.md, served by app_settings/whats_new to signed-in users).
 *
 * The copy held is kept in localStorage with the server's version of the file, so the About page and the
 * after-update sheet read it offline and a refresh downloads nothing while the file is unchanged. Refreshed on
 * sign-in and with every server version check. The sheet shows once per update: the entries newer than the
 * version this device last acknowledged (SEEN_KEY). A fresh install (no app state in storage yet) acknowledges
 * the running version without asking; an upgrade from a build that predates the notes has no SEEN_KEY either,
 * and gets the newest entry once.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private readonly held = new BehaviorSubject<Held>(load())
  /** Every entry held, newest first as the file lists them; the About page filters to its own version. */
  readonly entries$ = this.held.pipe(map(h => h.entries))
  /** The entries the after-update sheet should show now; null when there is nothing to announce. */
  readonly pending$ = new BehaviorSubject<readonly WhatsNewEntry[] | null>(null)

  constructor(private api: ApiService, store: Store) {
    if (!lastSeen() && freshInstall()) markSeen(app_version_number)
    store.select(UserState.getUser).pipe(map(u => !!u?.is_logged_in), distinctUntilChanged(), filter(Boolean)).subscribe(() => {
      this.refresh().catch(e => LoggerService.log(e))
    })
  }

  /** Asks the server for the file when it has changed, then announces anything this device has not seen. Throws when offline. */
  async refresh(): Promise<void> {
    try {
      const ret = await this.api.getVersionedList('app_settings/whats_new', this.held.value.version)
      if (ret?.rows) {
        const next: Held = { version: ret.version, entries: ret.rows as WhatsNewEntry[] }
        save(next)
        this.held.next(next)
      }
    } finally {
      this.announce()
    }
  }

  /** The sheet is closed and this version is acknowledged, so it is not shown again on this device. */
  dismiss(): void {
    markSeen(app_version_number)
    this.pending$.next(null)
  }

  private announce(): void {
    if (this.pending$.value) return
    const list = unseen(this.held.value.entries, app_version_number, lastSeen())
    if (list.length) this.pending$.next(list)
  }
}

function load(): Held {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    const h = raw ? JSON.parse(raw) : null
    return h && Array.isArray(h.entries) ? { version: String(h.version ?? ''), entries: h.entries } : NONE
  } catch {
    return NONE
  }
}

function save(h: Held): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(h))
  } catch (e) {
    LoggerService.log(e)
  }
}

/** True before the app has ever persisted its state on this device: the NGXS storage keys are not there yet. */
function freshInstall(): boolean {
  try {
    return !localStorage.getItem('rt2:user') && !localStorage.getItem('rt2:app_config')
  } catch {
    return true
  }
}

function lastSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY)
  } catch {
    return null
  }
}

function markSeen(version: string): void {
  try {
    localStorage.setItem(SEEN_KEY, version)
  } catch (e) {
    LoggerService.log(e)
  }
}
